import Link from "next/link";
import SectionHead from "@components/services/SectionHead";
import { resolveGuideLinks } from "@utils/guideLinks";

import "./guide-read-next.css";

/**
 * GuideReadNext — the end-of-page hand-over into the Agentic BI Guide.
 *
 * Sits under "Verwandte Services" (or the page's last content block) and
 * before the closing CTA on every service page, the services hub, home,
 * profiles, blog posts, webinars, success stories and the toolkit. The
 * mapping route → chapters lives in `src/data/components/guide-links.json`.
 *
 * Why it looks the way it does:
 *  • Real `<a>` links in the server HTML with the chapter H1 as anchor text —
 *    that is the ranking signal; a client-only block would not count.
 *  • The chapter's question sits under the anchor, so answer engines see
 *    question → page, not just a title.
 *  • One ItemList JSON-LD per block (`<page>#guide-kapitel`) names the
 *    recommended chapters as structured data, like the home page's
 *    related-content list.
 *  • Rows reuse the `.related` primitive of the services design system, so
 *    the block reads as a sibling of "Verwandte Services", not as an ad.
 *
 * `layout`:
 *  • "article" (default) — inside an ArticleLayout; same 64px top margin as
 *    the sibling sections.
 *  • "section" — standalone band with `.section` + `.container-tight` for
 *    pages that have no article column (home, Tailwind pages).
 *  • "bare" — no outer spacing; the caller wraps it.
 *
 * `surface`:
 *  • "card" — the block sits in the same white card as GuideContactCard, for
 *    templates that render it on a dark page background (the ABIS gradient
 *    behind /services/power-bi-mcp-server). Without it the navy headline and
 *    rows would be invisible there.
 */

const BASE_URL = "https://dbi-analytics.de";

interface GuideReadNextProps {
  /** The page's own route, e.g. "/services/power-bi-beratung" — the JSON key. */
  route: string;
  layout?: "article" | "section" | "bare";
  surface?: "card";
  className?: string;
}

const stripMarkdownLinks = (text: string) => text.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");

export default function GuideReadNext({ route, layout = "article", surface, className }: GuideReadNextProps) {
  const links = resolveGuideLinks(route);
  const pageUrl = `${BASE_URL}${route === "/" ? "/" : route}`;
  const headingId = "guide-kapitel-title";

  const itemList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": `${pageUrl}#guide-kapitel`,
    name: links.schemaName,
    description: stripMarkdownLinks(links.lede),
    inLanguage: "de-DE",
    itemListOrder: "https://schema.org/ItemListOrderAscending",
    numberOfItems: links.chapters.length,
    itemListElement: links.chapters.map((chapter, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: chapter.title,
      url: `${BASE_URL}${chapter.href}`,
    })),
  };

  const sectionClass = [
    "guide-next",
    layout === "section" ? "section guide-next--section" : "",
    surface === "card" ? "guide-next--card" : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  const body = (
    <>
      <SectionHead eyebrow={links.eyebrow} title={links.title} lede={links.lede} blue />
      <ul className={`related-grid guide-next__list guide-next__list--${links.chapters.length}`}>
        {links.chapters.map((chapter) => (
          <li key={chapter.href}>
            <Link className="related guide-next__item" href={chapter.href}>
              <span className="tg">{chapter.tag}</span>
              <h3>{chapter.title}</h3>
              <p>{chapter.question}</p>
              <span className="lnk">{links.readLabel} →</span>
            </Link>
          </li>
        ))}
      </ul>
      <p className="guide-next__all">
        <Link href={links.hub.href}>
          {links.allChaptersLabel} <span aria-hidden="true">→</span>
        </Link>
      </p>
    </>
  );

  const content = surface === "card" ? <div className="guide-next__card">{body}</div> : body;

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(itemList) }} />
      <section
        id="guide-kapitel"
        className={sectionClass}
        aria-labelledby={headingId}
        style={layout === "article" ? { marginTop: 64 } : undefined}
      >
        {layout === "section" ? <div className="container container-tight">{content}</div> : content}
      </section>
    </>
  );
}
